/**
 * Checkout of a reseller's shop (white-label, docs/white-label/CAPABILITY-MAP.md → tenant-payments).
 *
 * Empty on Traffic Creator itself, and then every checkout behaves exactly as before. When `methods` is
 * set, the checkout pages offer exactly those methods — the reseller's own FasterPay, PayPal, crypto,
 * bank or custom method (Martin, 2026-09-24: never Stripe for resellers) — and show the reseller's
 * transfer details instead of ours. Filled today by the reseller demo (components/reseller/demo-sandbox),
 * later by the tenant config the server sends for the reseller's domain.
 */
// brandConfig, not services/db: the demo boot imports this file before it seals the tab's storage, and db
// must not be evaluated that early.
import { useEffect, useState } from 'react';
import { brandConfig } from './brand';

export type TenantMethod = 'fasterpay' | 'paypal' | 'crypto' | 'bank' | 'custom';

export interface TenantCheckout {
  methods?: TenantMethod[];
  bank?: { holder: string; iban: string; bic: string; bankName: string; note: string };
  crypto?: { name: string; network: string; address: string; note: string };
  custom?: { name: string; how: string };
  /** A real reseller host (not our demo tab): orders go to the reseller's API. */
  live?: boolean;
}

export const tenantCheckout: TenantCheckout = {};

export const isTenantCheckout = () => Array.isArray(tenantCheckout.methods);

/** The reseller's transfer details as the server sends them (backend tenants/config.py pay_details). */
export interface PayDetailsView {
  bank?: { bankHolder: string; iban: string; bic: string; bankName: string } | null;
  crypto?: { wallet: string; network: string; asset: string } | null;
  customPay?: { name: string; how: string } | null;
}

let payDetailsLoaded = false;
let payDetails: Promise<boolean> | null = null;

export function applyPayDetails(v: PayDetailsView): void {
  tenantCheckout.bank = v.bank ? { holder: v.bank.bankHolder, iban: v.bank.iban, bic: v.bank.bic, bankName: v.bank.bankName, note: 'Use the reference so we can match your payment.' } : undefined;
  tenantCheckout.crypto = v.crypto ? { name: v.crypto.asset, network: v.crypto.network, address: v.crypto.wallet, note: `Send only ${v.crypto.asset} on ${v.crypto.network}.` } : undefined;
  if (v.customPay) tenantCheckout.custom = v.customPay;
  payDetailsLoaded = true;
}

/**
 * On a real reseller host the bank account, wallet and instructions are not in the page (anyone could read
 * them there); they come from /tenant/pay-details once the customer is signed in.
 */
export function loadTenantPayDetails(): Promise<boolean> {
  if (!tenantCheckout.live || payDetailsLoaded) return Promise.resolve(true);
  if (!payDetails) {
    payDetails = fetch(`${brandConfig.apiUrl}/tenant/pay-details`, { headers: authHeader() })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((v: PayDetailsView) => { applyPayDetails(v); return true; })
      .catch(() => { payDetails = null; return false; });
  }
  return payDetails;
}

/** True once the transfer details are there (always true outside a reseller shop). */
export function useTenantPayDetails(): boolean {
  const [ready, setReady] = useState(!tenantCheckout.live || payDetailsLoaded);
  useEffect(() => {
    if (ready) return undefined;
    let on = true;
    void loadTenantPayDetails().then((ok) => { if (on && ok) setReady(true); });
    return () => { on = false; };
  }, [ready]);
  return ready;
}

/** Shown in a shop while the details load: never our own account or wallet. */
export const PENDING_BANK = { holder: 'Loading…', iban: 'Loading…', bic: '…', bankName: '', note: 'Loading the payment details…' };
export const PENDING_CRYPTO = { name: 'Tether (USDT)', network: '…', address: 'Loading…', note: 'Loading the payment details…' };

/** Methods that send the customer to the provider's own payment page. */
export const REDIRECT_METHODS: TenantMethod[] = ['fasterpay', 'paypal'];

/**
 * Opens the reseller's FasterPay or PayPal checkout. The endpoint belongs to tenant-payments and pays into
 * the reseller's own account; nothing calls it outside a reseller shop.
 */
export async function startTenantCheckout(body: Record<string, unknown>): Promise<string> {
  const res = await fetch(`${brandConfig.apiUrl}/tenant/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(body),
  });
  if (!res.ok) return readError(res, 'The payment page could not be opened.');
  const data = await res.json();
  if (!data.url) throw new Error('The payment page could not be opened.');
  return data.url;
}

const authHeader = (): Record<string, string> => ({ Authorization: `Bearer ${localStorage.getItem('tgp_token') || ''}` });

async function readError(res: Response, fallback: string): Promise<never> {
  const data = await res.json().catch(() => ({}));
  const detail = typeof data.detail === 'string' ? data.detail : data.detail?.message;
  throw new Error(detail || fallback);
}

/**
 * Bank, crypto or the reseller's own method: creates the order and uploads the customer's proof in one call
 * (backend routers/tenant_public.py /tenant/orders/proof). Fields are the ones our checkout pages already
 * have: `tier`/`visitors`/`bulk_months` (visits), `type`/`credits` (SEO), `product`/`quantity` (YouTube).
 */
export async function submitTenantProof(fields: Record<string, string | number | undefined>, file: File): Promise<{ order: string; reference: string; amount: number }> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== '') form.append(k, String(v));
  form.append('file', file);
  const res = await fetch(`${brandConfig.apiUrl}/tenant/orders/proof`, { method: 'POST', headers: authHeader(), body: form });
  if (!res.ok) return readError(res, 'The confirmation could not be uploaded.');
  return res.json();
}

/** The reseller's coupons, checked server-side (backend /tenant/coupons/validate). */
export async function validateTenantCoupon(code: string, amount: number) {
  const res = await fetch(`${brandConfig.apiUrl}/tenant/coupons/validate?code=${encodeURIComponent(code)}&amount=${amount}`, { headers: authHeader() });
  if (!res.ok) return readError(res, 'This code is not valid.');
  return res.json() as Promise<{ code: string; discount_type: string; discount_value: number; calculated_discount: number }>;
}
